// Reading a CSV file of rows into the money table (design 02 8.3), in the
// browser. The file this app exports reads back as it is:
//   날짜,구분,금액,사람,분류,결제수단,메모
// Others are taken leniently: common header names, amounts with commas, ₩
// or 원, dates as 2026.10.05, 2026/10/05 or 2026-10-05. Without a 구분
// column a negative amount is 지출 and a positive one 수입, unless no
// amount is negative: then all are 지출. Separate 수입 and 지출 columns
// work too.
//
// Each line gets a client_id made from the file (name, size) and the line
// (number and text), so importing the same file again adds nothing.
import type { RowKind, TensorRowCreate } from './api'
import { validDay } from './format'

export const MEMO_MAX = 200
export const NAME_MAX = 30
export const AMOUNT_MAX = 100_000_000_000

/** Text of a file's bytes: UTF-8 (with or without a BOM), else EUC-KR/CP949. */
export function decodeCsv(bytes: Uint8Array): string {
  let text: string
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    text = new TextDecoder('euc-kr').decode(bytes)
  }
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
}

export interface CsvLine {
  /** 1-based line number of the record's first line in the file */
  line: number
  /** the record as it was in the file (for the client_id) */
  raw: string
  cells: string[]
}

/** RFC 4180 records: quoted cells may hold commas, quotes ("") and line breaks. */
export function parseCsv(text: string): CsvLine[] {
  const out: CsvLine[] = []
  let cells: string[] = []
  let cell = ''
  let quoted = false
  let line = 1
  let start = 0
  let startLine = 1
  const n = text.length
  const end = (i: number) => {
    cells.push(cell)
    const raw = text.slice(start, i)
    if (cells.length > 1 || cells[0] !== '') out.push({ line: startLine, raw, cells })
    cells = []
    cell = ''
  }
  for (let i = 0; i < n; i++) {
    const ch = text[i]!
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"'
          i++
        } else quoted = false
      } else {
        if (ch === '\n') line++
        cell += ch
      }
    } else if (ch === '"') {
      quoted = true
    } else if (ch === ',') {
      cells.push(cell)
      cell = ''
    } else if (ch === '\r' || ch === '\n') {
      end(i)
      if (ch === '\r' && text[i + 1] === '\n') i++
      line++
      start = i + 1
      startLine = line
    } else cell += ch
  }
  if (start < n || cell !== '' || cells.length > 0) end(n)
  return out
}

type Col = 'day' | 'kind' | 'amount' | 'income' | 'expense' | 'person' | 'category' | 'sub' | 'payment' | 'memo'

const HEADERS: Record<Col, string[]> = {
  day: ['날짜', '일자', '거래일', '거래일자', '거래일시', '일시', '사용일', '사용일자', 'date', 'day'],
  kind: ['구분', '수입/지출', '수입지출', '유형', '종류', '입출금', 'kind', 'type'],
  amount: ['금액', '액수', '거래금액', '이용금액', '사용금액', '결제금액', 'amount'],
  income: ['수입', '수입금액', '입금', '입금액', '입금금액', 'income'],
  expense: ['지출', '지출금액', '출금', '출금액', '출금금액', 'expense'],
  person: ['사람', '이름', '사용자', '사용한사람', '누가', 'person', 'who'],
  category: ['분류', '대분류', '카테고리', '항목', 'category'],
  sub: ['소분류', '세부분류', '하위분류', 'subcategory'],
  payment: ['결제수단', '결제', '수단', '카드', '지불수단', 'payment'],
  memo: ['메모', '내용', '비고', '적요', '사용처', '가맹점', '설명', 'memo', 'note'],
}

const norm = (h: string) => h.replace(/[\s_·.()]/g, '').toLowerCase()

/** Which column holds what (-1: none), from the header record. */
export function mapHeader(cells: string[]): Record<Col, number> | null {
  const cols = { day: -1, kind: -1, amount: -1, income: -1, expense: -1, person: -1, category: -1, sub: -1, payment: -1, memo: -1 }
  cells.forEach((c, i) => {
    const h = norm(c)
    for (const [col, names] of Object.entries(HEADERS) as [Col, string[]][]) {
      if (cols[col] < 0 && names.some((n) => norm(n) === h)) {
        cols[col] = i
        break
      }
    }
  })
  if (cols.day < 0 || (cols.amount < 0 && cols.income < 0 && cols.expense < 0)) return null
  return cols
}

/** A cell as text: trimmed, and the quote mark our export puts before a
    leading = + - @ (formula protection) taken off. */
export function cellText(s: string | undefined): string {
  const t = (s ?? '').trim()
  return /^'[=+\-@]/.test(t) ? t.slice(1) : t
}

/** "2026-10-05" of 2026-10-05, 2026.10.05, 2026/10/5, "2026. 10. 5.", 20261005,
    "2026년 10월 5일" (a time after it is left out); null when not a day. */
export function parseDay(s: string): string | null {
  const t = s.trim()
  let m = /^(\d{4})\s*[-./년]\s*(\d{1,2})\s*[-./월]\s*(\d{1,2})\s*[.일]?(?:\s.*|T.*)?$/.exec(t)
  if (!m) m = /^(\d{4})(\d{2})(\d{2})$/.exec(t)
  if (!m) return null
  const day = `${m[1]}-${m[2]!.padStart(2, '0')}-${m[3]!.padStart(2, '0')}`
  return validDay(day) ? day : null
}

/** Won as a whole number: "48,200", "₩48,200", "-1,000원", "(1,000)"; null when not one. */
export function parseWon(s: string): number | null {
  let t = s.replace(/[\s,₩원\\]/g, '').replace(/^KRW/i, '')
  let neg = false
  if (/^\(.*\)$/.test(t)) {
    neg = true
    t = t.slice(1, -1)
  }
  if (t.startsWith('-') || t.startsWith('−')) {
    neg = !neg
    t = t.slice(1)
  } else if (t.startsWith('+')) t = t.slice(1)
  if (!/^\d+(\.0+)?$/.test(t)) return null
  const n = Number(t.replace(/\.0+$/, ''))
  if (!Number.isSafeInteger(n)) return null
  return neg ? -n : n
}

function parseKind(s: string): RowKind | null {
  const t = s.trim().toLowerCase()
  if (['지출', '출금', '지급', '소비', 'expense', 'out', '-'].includes(t)) return 'expense'
  if (['수입', '입금', '소득', 'income', 'in', '+'].includes(t)) return 'income'
  return null
}

const chars = (s: string) => Array.from(s).length

function checkName(s: string, what: string, category: boolean): string | null {
  const parts = category ? s.split('>') : [s]
  if (parts.length > 2) return `${what}에는 '>'를 한 번만 쓸 수 있습니다`
  if (!category && s.includes('>')) return `${what}에는 '>'를 쓸 수 없습니다`
  for (const p of parts) {
    const t = p.trim()
    if (t === '') return `${what} 이름이 비어 있습니다`
    if (chars(t) > NAME_MAX) return `${what} 이름은 ${NAME_MAX}자까지입니다`
  }
  return null
}

/** 53 bits of a string's hash (cyrb53), as hex. */
function hash53(s: string, seed: number): string {
  let h1 = 0xdeadbeef ^ seed
  let h2 = 0x41c6ce57 ^ seed
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0')
}

/** The client_id of a line of a file: the same file gives the same ids. */
export function lineClientId(fileName: string, fileSize: number, line: number, raw: string): string {
  const s = `${fileName}\u0000${fileSize}\u0000${line}\u0000${raw}`
  return `csv-${hash53(s, 1)}${hash53(s, 2)}`
}

export type ImportRow = TensorRowCreate & Required<Pick<TensorRowCreate, 'kind' | 'day' | 'amount'>>

export interface ImportLine {
  line: number
  /** what will be sent; null when the line has an error */
  row: ImportRow | null
  error: string | null
}

export interface ImportPlan {
  /** why nothing can be read (no header, …) */
  error: string | null
  lines: ImportLine[]
  ok: number
  bad: number
}

/** Reads the text of a CSV file into rows to send (with errors per line). */
export function planImport(text: string, fileName: string, fileSize: number): ImportPlan {
  const records = parseCsv(text)
  const head = records[0]
  const cols = head ? mapHeader(head.cells) : null
  if (!head || !cols) {
    return {
      error: '첫 줄에 머리글(날짜, 구분, 금액, 사람, 분류, 결제수단, 메모)이 있어야 합니다.',
      lines: [],
      ok: 0,
      bad: 0,
    }
  }
  const at = (r: CsvLine, c: number) => (c >= 0 ? cellText(r.cells[c]) : '')
  const body = records.slice(1)
  // without 구분: are there negative amounts?
  const signed =
    cols.kind < 0 && cols.amount >= 0 && body.some((r) => (parseWon(at(r, cols.amount)) ?? 0) < 0)
  const lines: ImportLine[] = []
  for (const r of body) {
    const fail = (error: string) => lines.push({ line: r.line, row: null, error })
    const day = parseDay(at(r, cols.day))
    if (!day) {
      fail(`날짜를 읽을 수 없습니다: "${at(r, cols.day)}"`)
      continue
    }
    let amount: number | null = null
    let kind: RowKind | null = null
    if (cols.amount >= 0 && at(r, cols.amount) !== '') {
      amount = parseWon(at(r, cols.amount))
      if (amount === null) {
        fail(`금액을 읽을 수 없습니다: "${at(r, cols.amount)}"`)
        continue
      }
      if (cols.kind >= 0) {
        kind = parseKind(at(r, cols.kind))
        if (!kind) {
          fail(`구분은 지출이나 수입이어야 합니다: "${at(r, cols.kind)}"`)
          continue
        }
      } else kind = signed && amount > 0 ? 'income' : 'expense'
      amount = Math.abs(amount)
    } else {
      // separate 수입 and 지출 columns
      const inc = cols.income >= 0 && at(r, cols.income) !== '' ? parseWon(at(r, cols.income)) : null
      const exp = cols.expense >= 0 && at(r, cols.expense) !== '' ? parseWon(at(r, cols.expense)) : null
      if (exp) {
        amount = Math.abs(exp)
        kind = 'expense'
      } else if (inc) {
        amount = Math.abs(inc)
        kind = 'income'
      }
    }
    if (amount === null || kind === null || amount === 0) {
      fail('금액이 없습니다')
      continue
    }
    if (amount > AMOUNT_MAX) {
      fail('금액이 너무 큽니다 (1000억 원까지)')
      continue
    }
    const person = at(r, cols.person)
    let category = at(r, cols.category)
    const sub = at(r, cols.sub)
    if (sub !== '') category = category !== '' ? `${category}>${sub}` : sub
    category = category.replace(/\s*>\s*/g, '>')
    const payment = at(r, cols.payment)
    const memo = at(r, cols.memo)
    const bad =
      (person !== '' && checkName(person, '사람', false)) ||
      (category !== '' && checkName(category, '분류', true)) ||
      (payment !== '' && checkName(payment, '결제수단', false)) ||
      (chars(memo) > MEMO_MAX ? `메모는 ${MEMO_MAX}자까지입니다` : null)
    if (bad) {
      fail(bad)
      continue
    }
    lines.push({
      line: r.line,
      error: null,
      row: {
        client_id: lineClientId(fileName, fileSize, r.line, r.raw),
        kind,
        day,
        amount,
        ...(person !== '' ? { person } : {}),
        ...(category !== '' ? { category } : {}),
        ...(payment !== '' ? { payment } : {}),
        ...(memo !== '' ? { memo } : {}),
      },
    })
  }
  const ok = lines.filter((l) => l.row).length
  return { error: null, lines, ok, bad: lines.length - ok }
}

/** The rows to send, in batches of at most `size` (the server takes 500). */
export function batches<T>(rows: T[], size = 500): T[][] {
  const out: T[][] = []
  for (let i = 0; i < rows.length; i += size) out.push(rows.slice(i, i + size))
  return out
}
